import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Users, Package, Star } from "lucide-react";
import api from "../lib/api";
import { Spinner, vibrantTheme } from "./common";
import PopSectionTabs from "./PopSectionTabs";

const LIGHT_SHADES = ["#EFF6FF", "#ECFDF5", "#F5F3FF", "#FFF7ED", "#FEFCE8", "#FDF2F8", "#F0FDFA"];
const shadeFor = (s) => LIGHT_SHADES[[...(s || "")].reduce((n, c) => n + c.charCodeAt(0), 0) % LIGHT_SHADES.length];

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-3.5 py-2 text-sm font-700 border transition-colors ${active ? "bg-primary text-white border-primary" : "border-zinc-300 bg-white text-zinc-700 hover:border-primary"}`}>
    {children}
  </button>
);

export function AirportCard({ a, accent }) {
  return (
    <Link to={`/airports/${a.iata}`} data-testid={`airport-pop-card-${a.iata}`}
      className="group border border-zinc-300 p-4 flex gap-3 hover:-translate-y-px hover:border-primary transition-[transform,border-color]"
      style={{ backgroundColor: shadeFor(a.iata + a.name) }}>
      <div className="w-14 h-14 shrink-0 text-white flex items-center justify-center font-mono font-700 text-base"
        style={{ backgroundColor: accent }}>{a.iata}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="font-700 text-base text-zinc-900 group-hover:text-primary transition-colors truncate">{a.name}</div>
          {a.rank && <span className="font-mono text-sm font-700 text-white px-2 py-0.5 shrink-0" style={{ backgroundColor: accent }}>#{a.rank}</span>}
        </div>
        <div className="flex items-center gap-2 text-sm text-zinc-600 mt-1 flex-wrap">
          <span className="inline-flex items-center gap-0.5"><MapPin className="w-4 h-4 shrink-0" />{[a.city, a.state || a.country].filter(Boolean).join(", ")}</span>
          {a.passengers && <span className="inline-flex items-center gap-1 font-700" style={{ color: accent }}><Users className="w-4 h-4" />{a.passengers}/yr</span>}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2 items-center">
          {a.hub && <span className="text-sm bg-white/80 border border-zinc-200 text-zinc-700 px-2 py-0.5">{a.hub}</span>}
          {a.role && <span className="text-sm bg-white/80 border border-zinc-200 text-zinc-700 px-2 py-0.5">{a.role}</span>}
          {a.region && <span className="text-sm bg-white/80 border border-zinc-200 text-zinc-700 px-2 py-0.5">{a.region}</span>}
          {a.mega_hub && <span className="text-sm inline-flex items-center gap-1 font-700 text-amber-700"><Star className="w-4 h-4 fill-amber-500 text-amber-500" /> Major European Hub</span>}
          {a.strength && <span className="text-sm inline-flex items-center gap-1 text-zinc-700"><Package className="w-4 h-4" style={{ color: accent }} />{a.strength}</span>}
        </div>
      </div>
    </Link>
  );
}

const Grid = ({ items, accent }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
    {items.map((a) => <AirportCard key={a.iata + (a.rank || "")} a={a} accent={accent} />)}
  </div>
);

const SectionHeader = ({ title, note, accent }) => (
  <div className="text-white px-5 py-4 flex items-center justify-between flex-wrap gap-2 scroll-mt-32" style={{ backgroundColor: accent }}>
    <h2 className="font-heading font-900 text-2xl">{title}</h2>
    <span className="text-sm font-mono text-white/85">{note}</span>
  </div>
);

const CargoHeading = ({ accent }) => (
  <h3 className="font-heading font-900 text-lg mt-8 mb-3 flex items-center gap-2">
    <span className="w-2 h-5 inline-block" style={{ backgroundColor: accent }} /> Cargo Powerhouses
  </h3>
);

export default function PopularAirports() {
  const [data, setData] = useState(null);
  const [euRegion, setEuRegion] = useState("");
  const [glRegion, setGlRegion] = useState("");
  const [showAllUs, setShowAllUs] = useState(false);
  useEffect(() => { api.get("/airports-popular").then((r) => setData(r.data)).catch(() => setData(false)); }, []);

  const usTheme = vibrantTheme("pop-us"), euTheme = vibrantTheme("pop-europe"), glTheme = vibrantTheme("pop-global");

  const euItems = useMemo(() => {
    if (!data) return [];
    if (!euRegion) return data.europe.top;
    const byIata = Object.fromEntries(data.europe.top.map((a) => [a.iata, a]));
    return (data.europe.regions[euRegion] || [])
      .map((code, i) => {
        const base = byIata[code] || data.europe.extras[code];
        return base ? { ...base, rank: i + 1 } : null;
      }).filter(Boolean);
  }, [data, euRegion]);

  const glItems = useMemo(() => {
    if (!data) return [];
    return glRegion ? data.global.regions.filter((r) => r.name === glRegion) : data.global.regions;
  }, [data, glRegion]);

  if (data === null) return <Spinner />;
  if (data === false) return <p className="text-muted-foreground py-10">Could not load the popularity directory.</p>;

  const usShown = showAllUs ? data.us.top : data.us.top.slice(0, 15);

  return (
    <div data-testid="popular-airports-view">
      <PopSectionTabs prefix="airports" />
      <div className="space-y-14">
        <section id="us" data-testid="us-airports-section">
          <SectionHeader title="US Airports" note="Ranked by annual passenger traffic (ACI-NA / FAA)" accent={usTheme.bg} />
          <div className="mt-4">
            <Grid items={usShown} accent={usTheme.bg} />
            {!showAllUs && (
              <button onClick={() => setShowAllUs(true)} data-testid="show-all-us-airports"
                className="mt-4 px-5 py-2.5 text-base font-700 border border-zinc-300 bg-white hover:border-primary hover:text-primary transition-colors">
                Show all {data.us.top.length} US airports
              </button>
            )}
            <CargoHeading accent={usTheme.bg} />
            <p className="text-base text-zinc-600 mb-3">The busiest US cargo airports — a different list from passenger traffic.</p>
            <Grid items={data.us.cargo} accent={usTheme.bg} />
          </div>
        </section>

        <section id="europe" data-testid="europe-airports-section">
          <SectionHeader title="European Airports" note="Ranked by annual passenger traffic (ACI Europe)" accent={euTheme.bg} />
          <div className="flex flex-wrap gap-1.5 mt-4 mb-3">
            <Chip active={!euRegion} onClick={() => setEuRegion("")} testid="eu-airport-region-all">All of Europe</Chip>
            {data.europe.region_names.map((r) => (
              <Chip key={r} active={euRegion === r} onClick={() => setEuRegion(r)}
                testid={`eu-airport-region-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{r}</Chip>
            ))}
          </div>
          <Grid items={euItems} accent={euTheme.bg} />
          {!euRegion && (
            <>
              <CargoHeading accent={euTheme.bg} />
              <Grid items={data.europe.cargo} accent={euTheme.bg} />
            </>
          )}
        </section>

        <section id="global" data-testid="global-airports-section">
          <SectionHeader title="Other Global Airports" note="Ranked by region — Asia, Middle East, Africa, Americas & Pacific" accent={glTheme.bg} />
          <div className="flex flex-wrap gap-1.5 mt-4 mb-3">
            <Chip active={!glRegion} onClick={() => setGlRegion("")} testid="gl-airport-region-all">All regions</Chip>
            {data.global.regions.map((r) => (
              <Chip key={r.name} active={glRegion === r.name} onClick={() => setGlRegion(r.name)}
                testid={`gl-airport-region-${r.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{r.name}</Chip>
            ))}
          </div>
          {glItems.map((r) => (
            <div key={r.name} className="mb-8" data-testid={`gl-region-group-${r.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <h3 className="font-heading font-900 text-lg mb-3 flex items-center gap-2">
                <span className="w-2 h-5 inline-block" style={{ backgroundColor: glTheme.bg }} /> {r.name}
              </h3>
              <Grid items={r.items} accent={glTheme.bg} />
            </div>
          ))}
          {!glRegion && (
            <>
              <CargoHeading accent={glTheme.bg} />
              <p className="text-base text-zinc-600 mb-3">The world's most important cargo airports — a very different list from passenger traffic.</p>
              <Grid items={data.global.cargo} accent={glTheme.bg} />
            </>
          )}
        </section>
      </div>
    </div>
  );
}
